import { usePostCommunicationContext } from '../../PostCommunicationProvider';
import CommentsApi from 'app/modules/comments/api';
import { queryClient } from 'api/client';
import { queryKey } from 'constants/queryKey';
import { InfiniteData, useMutation } from 'react-query';
import { Comment } from 'app/modules/comments/types';

interface UseAnnotationDone {
  markAsDone: () => void;
}

function updateCommentAnnotationDone(
  comment: Comment,
): (
  old: InfiniteData<ApiResponse<Comment[]>> | undefined,
) => InfiniteData<ApiResponse<Comment[]>> | undefined {
  return (old) => {
    if (!old?.pages) return old;

    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        data: page.data.map((c) =>
          c.id === comment.id && c.meta?.annotation
            ? {
                ...c,
                meta: {
                  ...c.meta,
                  annotation: { ...c.meta.annotation, done: true },
                },
              }
            : c,
        ),
      })),
    };
  };
}

const useAnnotationDone = (comment: Comment): UseAnnotationDone => {
  const { postId } = usePostCommunicationContext();

  const setAnnotationDone = useMutation(
    () => {
      return CommentsApi.setAnnotationDone({
        postId,
        comment_id: comment.id,
        done: true,
      });
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(queryKey.postCommentsThread());

        queryClient.setQueriesData<
          InfiniteData<ApiResponse<Comment[]>> | undefined
        >(queryKey.postCommentsThread(), updateCommentAnnotationDone(comment));
      },
      retry: false,
    },
  );

  return {
    markAsDone: setAnnotationDone.mutate,
  };
};

export default useAnnotationDone;
